import { forwardRef } from "react";
import { useTranslation, type WithTranslation } from "react-i18next";
import { Divider, Stack, Typography, type SxProps } from "~/common/mui";
import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import { Price } from "~/domains/pricing/components/Price/v3/Price";
import type { NetPriceData, TotalPriceData, WithSurcharge } from "~/domains/pricing/types/PriceData";
import { getTotalPrice } from "~/domains/product/utils/productDescriptorUtils";
import { volatileSum } from "~/utils/PriceUtils";
import { uniqueValues } from "~/common/utils/misc";
import type { QuoteLineMO } from "~/features/orders/common/components/v2/models";
import type { RenderCboxFn } from "~/features/orders/common/components/v2/QuoteTable/types";
import { getEDD } from "~/features/orders/common/components/v2/QuoteTable/utils";
import { Param, TooltipParam } from "../common/components/v2/Params";
import {
  AlternativeLineHeader,
  BorderLeftWrapper,
  QuoteAlternateLineItem,
} from "../common/components/v2/OrderAndQuoteSharedStyles";
import { ProductHeader } from "./ProductHeader";
import Tag from "~/common/components/Tag/Tag";
import { getQuoteLineStatus } from "../common/utils/util";

interface Props {
  quoteLine: QuoteLineMO;
  variant?: "main" | "alternative";
  forceRender?: boolean;
  hidden?: boolean;
  renderCheckbox?: RenderCboxFn;
  sx?: SxProps;
  showDivider?: boolean;
}

interface SumPartsRow {
  key: string;
  title: React.ReactNode;
  confirmedQuantity: number;
  requestedQuantity: number;
  estimatedDeliveryDate: string;
  requestedDeliveryDate: string;
  price?: NetPriceData;
  totalPrice: TotalPriceData & WithSurcharge;
  status?: React.ReactNode;
}

export const QuotePartsList = forwardRef<HTMLDivElement, Props>(
  ({ quoteLine, variant = "main", forceRender, renderCheckbox, hidden, showDivider }, ref) => {
    const { t } = useTranslation();

    if (!forceRender && variant === "main" && quoteLine.parts.length < 2) {
      return null;
    }

    if (hidden) {
      return null;
    }

    const { header, parts } = sumParts({ quoteLine, variant, t });
    if (variant === "main") {
      header.title = <Typography>{t("quoteList.quote.line.number")}</Typography>;
    } else {
      header.title = (
        <Typography>
          {t("quoteList.alternative.quote.line.number", {
            altQuoteLineNumber: quoteLine.alternativeTo,
          })}
        </Typography>
      );
    }

    const quoteLineList = parts.map((part) => (
      <QuoteAlternateLineItem key={part.key} hideLeftBorder={variant == "main"} ref={ref}>
        <Param label={header.title} value={part.title} />
        <TooltipParam
          label={t("quoteListPage.table.header.confirmedQuantity")}
          value={t("common.of.text.with.values", {
            firstValue: part.confirmedQuantity,
            secondValue: part.requestedQuantity,
          })}
          tooltipContent={
            <Stack gap={0.5}>
              <Param label={t("quoteListPage.table.header.confirmedQuantity")} value={part.confirmedQuantity} />
              <Param label={t("quoteListPage.table.header.requestedQuantity")} value={part.requestedQuantity} />
            </Stack>
          }
        />
        <Param label={t("quoteListPage.table.header.estimatedDeliveryDate")} value={part.estimatedDeliveryDate} />
        <Param
          label={t("wishlistPage.table.header.priceUnit")}
          value={<Price price={part.price} zeroText={t("product.priceOnRequest")} surchargeEnabled />}
          footer={<Price.NetPriceUnit price={part.price} variant="short" surchargeEnabled />}
        />
        <Param
          label={t("quoteDetailsPage.table.header.total")}
          value={<Price as="b" price={part.totalPrice} surchargeEnabled />}
          footer={<Price.TotalPriceUnit price={part.totalPrice} variant="short" surchargeEnabled={false} />}
        />
      </QuoteAlternateLineItem>
    ));

    if (variant == "alternative") {
      return (
        <Stack gap={1} bgcolor={"surfaceContainer.low"}>
          <AlternativeLineHeader>
            <Stack direction={"row"} gap={1} alignItems={"center"}>
              {renderCheckbox?.(quoteLine)}
              <Typography variant="labelLarge" fontWeight={"normal"}>
                {header.title}
              </Typography>
            </Stack>
            <Tag variant={getQuoteLineStatus(quoteLine.status)}>
              {t(`codes.quoteLineItemStatus.${quoteLine.status}`)}
            </Tag>
          </AlternativeLineHeader>
          <Stack gap={1} paddingBottom={2}>
            <BorderLeftWrapper variant={variant}>
              <ProductHeader quoteLine={quoteLine} />
            </BorderLeftWrapper>
            <Stack gap={1} marginLeft={2}>
              {quoteLineList}
            </Stack>
            {showDivider && <Divider sx={{ mx: 2 }} />}
          </Stack>
        </Stack>
      );
    }
    return <>{quoteLineList}</>;
  },
);

QuotePartsList.displayName = "QuotePartsTable";

function sumParts({
  quoteLine,
  variant,
  t,
}: Props & {
  t: WithTranslation["t"];
}) {
  const header: Partial<SumPartsRow> = {
    key: "header",
  };

  if (variant === "main") {
    header.title = <strong>{t("quoteList.main.quote.line.number")}</strong>;
  } else {
    header.title = (
      <Typography>
        {t("quoteList.alternative.quote.line.number", {
          altQuoteLineNumber: quoteLine.alternativeTo,
        })}
      </Typography>
    );
  }

  header.confirmedQuantity = quoteLine.parts.reduce((acc, part) => acc + part.confirmedQuantity, 0);
  header.requestedQuantity = quoteLine.parts[0].requestedQuantity;

  const edds = uniqueValues(quoteLine.parts.map(getEDD));
  header.estimatedDeliveryDate = edds[0] + (edds.length > 1 ? ", ..." : "");
  header.requestedDeliveryDate = formatNullableDate(quoteLine.parts[0].requestedDeliveryDate, DateFormatType.DATE);

  const parts = quoteLine.parts.map((part) => ({
    key: part.lineNo,
    title: `${quoteLine.lineNo}:${part.lineNo}`,
    confirmedQuantity: part.confirmedQuantity,
    requestedQuantity: part.requestedQuantity,
    estimatedDeliveryDate: getEDD(part),
    requestedDeliveryDate: formatNullableDate(part.requestedDeliveryDate, DateFormatType.DATE),
    price: part.price,
    totalPrice: getTotalPrice(part.price, part.confirmedQuantity),
  }));

  if (parts.length === 1) {
    header.price = parts[0].price;
    header.totalPrice = parts[0].totalPrice;
  } else {
    const sumResult = volatileSum(...parts.map((part) => part.totalPrice));
    header.totalPrice = {
      ...sumResult,
      baseValue: sumResult.value,
      surcharge: 0,
      isSubscription: parts[0].price?.isSubscription ?? false,
    };
  }

  header.status = <Tag>{t(`codes.quoteLineItemStatus.${quoteLine.status}`)}</Tag>;

  return {
    header,
    parts: parts.length > 1 ? parts : [],
  } as unknown as {
    header: SumPartsRow;
    parts: SumPartsRow[];
  };
}
